import React, { useState, useEffect, useRef } from 'react';

interface Slide {
  image: string;
  title: string;
  subtitle: string;
}

const Hero: React.FC = () => {
  const [currentSlide, setCurrentSlide] = useState<number>(0);
  const [progress, setProgress] = useState<number>(0);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  // Images du carrousel - Photos Podium
  const slides: Slide[] = [
    {
      image: '/Carousel/1.svg',
      title: 'Performance',
      subtitle: 'Excellence & Précision'
    },
    {
      image: '/Carousel/2.svg',
      title: 'Innovation',
      subtitle: 'Technologie de pointe'
    },
    {
      image: '/Carousel/3.svg',
      title: 'Design',
      subtitle: 'Esthétique & Fonctionnalité'
    },
    {
      image: '/Carousel/4.svg',
      title: 'Sur Mesure',
      subtitle: 'Chaque détail compte'
    },
    {
      image: '/Carousel/5.svg',
      title: 'Haute Performance',
      subtitle: 'Dépassez vos limites'
    },
    {
      image: '/Carousel/6.svg',
      title: 'Expertise',
      subtitle: 'Savoir-faire artisanal'
    },
    {
      image: '/Carousel/7.svg',
      title: 'Excellence',
      subtitle: 'Le meilleur pour vous'
    }
  ];

  // Défilement automatique avec barre de progression
  useEffect(() => {
    const duration = 5000; // 5 secondes
    const updateInterval = 50; // Mise à jour toutes les 50ms pour animation fluide
    let elapsed = 0;

    // Réinitialiser la progression
    setProgress(0);

    // Mettre à jour la progression
    const progressTimer = setInterval(() => {
      elapsed += updateInterval;
      const newProgress = (elapsed / duration) * 100;

      if (newProgress >= 100) {
        setProgress(100);
      } else {
        setProgress(newProgress);
      }
    }, updateInterval);

    // Changer de slide après 5 secondes
    intervalRef.current = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
      setProgress(0);
    }, duration);

    return () => {
      clearInterval(progressTimer);
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [currentSlide, slides.length]);

  const goToSlide = (index: number): void => {
    setCurrentSlide(index);
    setProgress(0);
  };

  const nextSlide = (): void => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
    setProgress(0);
  };

  const prevSlide = (): void => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
    setProgress(0);
  };

  return (
    <section className="relative bg-white pt-16 md:pt-20">
      <div className="max-w-full">
        {/* Carrousel - Full width comme Satisfy */}
        <div className="relative h-[500px] md:h-[650px] lg:h-[750px] overflow-hidden">
          {/* Images */}
          {slides.map((slide, index) => (
            <div
              key={index}
              className={`absolute inset-0 transition-opacity duration-1000 ease-in-out ${index === currentSlide ? 'opacity-100' : 'opacity-0'
                }`}
            >
              <img
                src={slide.image}
                alt={slide.title}
                className="w-full h-full object-cover"
              />
              {/* Overlay très subtil */}
              <div className="absolute inset-0 bg-black/20"></div>

              {/* Texte sur l'image - Style minimaliste */}
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="text-center text-white px-4 max-w-4xl">
                  <h2 className="text-3xl md:text-5xl lg:text-7xl font-bold mb-4 drop-shadow-2xl tracking-wider font-eurostile uppercase">
                    {slide.title}
                  </h2>
                  <p className="text-sm md:text-base lg:text-lg font-light drop-shadow-lg tracking-wide uppercase">
                    {slide.subtitle}
                  </p>
                </div>
              </div>
            </div>
          ))}

          {/* Boutons de navigation - Style minimaliste */}
          <button
            onClick={prevSlide}
            className="absolute left-4 md:left-8 top-1/2 -translate-y-1/2 text-white hover:opacity-70 transition-opacity duration-300 p-2"
            aria-label="Image précédente"
          >
            <svg className="w-8 h-8 md:w-10 md:h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <button
            onClick={nextSlide}
            className="absolute right-4 md:right-8 top-1/2 -translate-y-1/2 text-white hover:opacity-70 transition-opacity duration-300 p-2"
            aria-label="Image suivante"
          >
            <svg className="w-8 h-8 md:w-10 md:h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
            </svg>
          </button>

          {/* Indicateurs (points) - Avec barre de chargement */}
          <div className="absolute bottom-8 md:bottom-12 left-1/2 -translate-x-1/2 flex space-x-1.5">
            {slides.map((_, index) => (
              <button
                key={index}
                onClick={() => goToSlide(index)}
                className="relative w-8 md:w-10 h-0.5 bg-white/40 hover:bg-white/60 transition-colors duration-300 overflow-hidden"
                aria-label={`Aller à l'image ${index + 1}`}
              >
                {/* Barre de progression pour la slide active */}
                {index === currentSlide && (
                  <div
                    className="absolute top-0 left-0 h-full bg-white transition-all duration-100 ease-linear"
                    style={{ width: `${progress}%` }}
                  />
                )}
                {/* Barre pleine pour les slides déjà vues */}
                {index < currentSlide && (
                  <div className="absolute top-0 left-0 h-full w-full bg-white" />
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Hero Text - Style épuré */}
        <div className="mt-8 md:mt-12 text-center max-w-3xl mx-auto px-6 pb-10 md:pb-10">
          <p className="text-sm md:text-base text-black leading-relaxed tracking-wide uppercase font-light">
            La nouvelle référence vélo route, triathlon & gravel haut de gamme, spécialisé dans le montage sur mesure et la vente de composants premium
          </p>
        </div>
      </div>
    </section>
  );
};

export default Hero;

